<script setup lang="ts">
import { SolButton } from '@solfacil/girassol'
import { useI18n } from 'vue-i18n'
import CheckCircleIcon from '~icons/material-symbols/check-circle'
import CircleIcon from '~icons/material-symbols/circle'
import ErrorIcon from '~icons/material-symbols/error'
import InfoIcon from '~icons/material-symbols/info'
import WarningIcon from '~icons/material-symbols/warning'
import { FEATURE_FLAGS } from '~/constants'
import useAuth from '~/store-v2/auth/useAuth'
import { useFeatureFlag } from '~/store-v2/feature-flag/useFeatureFlag'
import usePartner from '~/store-v2/partners/usePartner'
import { useImpersonateStore } from '~/store/impersonate'
import { useBlocklistAlert } from '~/ui-blocks/overview/Notifications/useBlocklistAlert'

interface AlertData {
  type: 'warning' | 'info' | 'error' | 'success' | 'default'
  title: string
  description?: string
  actionLabel?: string
  priority: number
  onActionClick?: () => void
}

const { t } = useI18n()
const { data: auth } = useAuth()
const impersonateStore = useImpersonateStore()

const basePartnerId = computed(() => {
  const perfil = auth.value?.me?.perfil
  if (perfil === 'admin' && impersonateStore.partnerId > 0) {
    return impersonateStore.partnerId.toString()
  }
  return auth.value?.person?.partner?.id?.toString() || ''
})

const { data: partnerData } = usePartner(basePartnerId)

const {
  userIsOnBlockedList,
  blocklistTitle,
  blocklistDescription,
  blocklistType,
  blocklistActionLabel,
  handleBlocklistActionClick,
} = useBlocklistAlert(partnerData, basePartnerId)

const { data: temporaryInstabilityFlag } = useFeatureFlag<boolean>(FEATURE_FLAGS.TEMPORARY_INSTABILITY, false, true)

const systemInstabilityAlert = computed<AlertData | null>(() => {
  if (!temporaryInstabilityFlag.value) return null

  return {
    type: 'warning',
    title: t('app.alert.temporary_instability.title'),
    description: t('app.alert.temporary_instability.description'),
    priority: 10,
  }
})

const blocklistAlert = computed<AlertData | null>(() => {
  if (!userIsOnBlockedList.value) return null

  return {
    type: blocklistType.value,
    title: blocklistTitle.value,
    description: blocklistDescription.value,
    actionLabel: blocklistActionLabel.value,
    priority: 9,
    onActionClick: handleBlocklistActionClick,
  }
})

const activeAlert = computed<AlertData | null>(() => {
  const alerts: AlertData[] = []

  if (blocklistAlert.value) {
    alerts.push(blocklistAlert.value)
  }

  if (systemInstabilityAlert.value) {
    alerts.push(systemInstabilityAlert.value)
  }

  if (alerts.length === 0) return null

  return alerts.sort((a, b) => b.priority - a.priority)[0]
})

const iconMap = {
  warning: WarningIcon,
  info: InfoIcon,
  error: ErrorIcon,
  success: CheckCircleIcon,
  default: CircleIcon,
}

const IconComponent = computed(() => {
  if (!activeAlert.value) return CircleIcon
  return iconMap[activeAlert.value.type]
})

function handleActionClick() {
  if (activeAlert.value?.onActionClick) {
    activeAlert.value.onActionClick()
  }
}

const hasActiveAlert = computed(() => !!activeAlert.value)

defineExpose({
  hasActiveAlert,
})
</script>

<template>
  <div
    v-if="hasActiveAlert && activeAlert"
    class="top-alert"
    :class="`top-alert--${activeAlert.type}`"
  >
    <div class="top-alert-main">
      <component
        :is="IconComponent"
        class="top-alert-icon"
      />
      <div class="top-alert-content">
        <div class="top-alert-text">
          <span class="top-alert-title">{{ activeAlert.title }}:</span>
          <span
            v-if="activeAlert.description"
            class="top-alert-description"
          >{{ activeAlert.description }}</span>
        </div>
      </div>
    </div>
    <SolButton
      v-if="activeAlert.actionLabel"
      id="top-alert-action"
      class="top-alert-button"
      size="small"
      variant="secondary"
      @click="handleActionClick"
    >
      {{ activeAlert.actionLabel }}
    </SolButton>
  </div>
</template>

<style scoped lang="scss">
.top-alert {
  @apply flex flex-col gap-3 px-4 py-2 w-full;
  @apply border-b border-solid;

  @screen sm:system {
    @apply flex-row items-center gap-4;
  }

  &-main {
    @apply flex gap-4 items-start flex-1 min-w-0;

    @screen sm:system {
      @apply items-center;
    }
  }

  &-icon {
    @apply w-6 h-6 flex-shrink-0;
  }

  &-content {
    @apply flex flex-1 min-w-0;
  }

  &-text {
    @apply flex flex-col gap-1 leading-[1.15] not-italic flex-1 min-w-0;
  }

  &-title {
    @apply text-brand-primary-pure fonts-body-medium-bold;
  }

  &-description {
    @apply text-brand-primary-pure fonts-body-medium-regular;
  }

  &-button {
    @apply flex-shrink-0 w-full;

    @screen sm:system {
      @apply w-auto;
    }
  }

  &--warning {
    @apply bg-feedback-warning-light border-feedback-warning-medium;

    .top-alert-title {
      @apply text-brand-primary-pure fonts-body-medium-bold;
    }

    .top-alert-description {
      @apply text-brand-primary-pure fonts-body-medium-regular;
    }

    .top-alert-icon {
      @apply text-feedback-warning-pure;
    }
  }

  &--info {
    @apply bg-feedback-informative-light border-feedback-informative-medium;

    .top-alert-icon {
      @apply text-feedback-informative-pure;
    }
  }

  &--error {
    @apply bg-feedback-negative-light border-feedback-negative-medium;

    .top-alert-icon {
      @apply text-feedback-negative-pure;
    }
  }

  &--success {
    @apply bg-feedback-positive-light border-feedback-positive-medium;

    .top-alert-icon {
      @apply text-feedback-positive-pure;
    }
  }

  &--default {
    @apply bg-neutral-high-pure border-neutral-high-medium;

    .top-alert-icon {
      @apply text-neutral-low-medium;
    }
  }
}
</style>
